import { Component } from '@theme/component';
import { Scroller } from '@theme/scrolling';

/**
 * Compact catalogue product links using the theme's Scroller and component lifecycle.
 * @typedef {Object} CatalogueProductRefs
 * @property {HTMLUListElement} scroller
 * @property {HTMLLIElement[]} [cards]
 * @property {HTMLElement} [controls]
 * @property {HTMLElement} [counter]
 * @property {HTMLElement} [status]
 * @property {HTMLButtonElement} [autoplayButton]
 * @extends {Component<CatalogueProductRefs>}
 */
class CatalogueProductCarousel extends Component {
  requiredRefs = ['scroller'];
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {Scroller | undefined} */
  #scroll;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;
  /** @type {MediaQueryList | undefined} */
  #motion;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  #index = 0;
  #paused = false;
  #hovered = false;
  #focused = false;
  #visible = false;
  #pointerActive = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#cleanup();
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#hovered = this.matches(':hover');
    this.#focused = this.contains(document.activeElement);
    this.#visible = false;
    this.#pointerActive = false;
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#scroll = new Scroller(scroller, {
      onScroll: () => { if (!signal.aborted) this.#sync(); },
      onScrollStart: () => { if (!signal.aborted) this.#stop(); },
      onScrollEnd: () => { if (!signal.aborted) this.#sync(); },
    });

    this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    scroller.addEventListener('pointerdown', this.#onPointerDown, { signal });
    window.addEventListener('pointerup', this.#onPointerUp, { signal });
    window.addEventListener('pointercancel', this.#onPointerUp, { signal });
    document.addEventListener('visibilitychange', this.#schedule, { signal });
    this.#motion.addEventListener('change', this.#sync, { signal });

    this.#resizeObserver = new ResizeObserver(this.#sync);
    this.#resizeObserver.observe(scroller);
    this.#intersectionObserver = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.#schedule();
    }, { threshold: 0.05 });
    this.#intersectionObserver.observe(this);
    this.#sync();
  }

  #cleanup() {
    this.#stop();
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#scroll?.destroy();
    this.#controller = undefined;
    this.#resizeObserver = undefined;
    this.#intersectionObserver = undefined;
    this.#scroll = undefined;
  }

  get #cards() {
    return this.refs.cards?.filter((card) => card instanceof HTMLElement) ?? [];
  }

  get #rtl() {
    return getComputedStyle(this.refs.scroller).direction === 'rtl';
  }

  get #overflow() {
    const { scroller } = this.refs;
    return scroller.scrollWidth > scroller.clientWidth + 2;
  }

  get #atStart() {
    return Math.abs(this.refs.scroller.scrollLeft) < 2;
  }

  get #atEnd() {
    const { scroller } = this.refs;
    return Math.abs(scroller.scrollLeft) >= scroller.scrollWidth - scroller.clientWidth - 2;
  }

  #sync = () => {
    if (!this.isConnected || !this.#controller || this.#controller.signal.aborted) return;
    const { scroller, counter, controls, autoplayButton } = this.refs;
    const bounds = scroller.getBoundingClientRect();
    const edge = this.#rtl ? bounds.right : bounds.left;
    let distance = Infinity;
    this.#cards.forEach((card, index) => {
      const rectangle = card.getBoundingClientRect();
      const currentDistance = Math.abs((this.#rtl ? rectangle.right : rectangle.left) - edge);
      if (currentDistance < distance) {
        distance = currentDistance;
        this.#index = index;
      }
    });
    if (counter) counter.textContent = `${this.#index + 1} / ${this.#cards.length}`;
    if (controls) controls.hidden = !this.#overflow;
    if (autoplayButton) {
      autoplayButton.hidden = this.dataset.autoplay !== 'true' || Boolean(this.#motion?.matches);
      autoplayButton.setAttribute('aria-label', this.#paused ? autoplayButton.dataset.playLabel ?? '' : autoplayButton.dataset.pauseLabel ?? '');
    }
    this.toggleAttribute('data-paused', this.#paused);
    this.#schedule();
  };

  #stop = () => {
    clearTimeout(this.#timer);
    this.#timer = undefined;
  };

  #schedule = () => {
    this.#stop();
    if (!this.isConnected || !this.#controller || this.#controller.signal.aborted) return;
    if (this.dataset.autoplay !== 'true' || this.#paused || this.#hovered || this.#focused || !this.#visible || this.#pointerActive || document.hidden || this.#motion?.matches || !this.#overflow) return;
    const seconds = Math.max(3, Math.min(8, Number(this.dataset.interval) || 4));
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      this.#advance(1, false);
      this.#schedule();
    }, seconds * 1000);
  };

  /** @param {Event} event */
  next(event) {
    event.preventDefault();
    this.#advance(1, true);
  }

  /** @param {Event} event */
  previous(event) {
    event.preventDefault();
    this.#advance(-1, true);
  }

  /** @param {Event} event */
  toggleAutoplay(event) {
    event.preventDefault();
    this.#paused = !this.#paused;
    this.#sync();
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (!this.#cards.length) return;
    const target = event.composedPath()[0];
    const moveFocus = target instanceof Element && Boolean(target.closest('a'));
    const focusedIndex = moveFocus && target instanceof Element ? this.#cards.findIndex((card) => card.contains(target)) : -1;
    const currentIndex = focusedIndex >= 0 ? focusedIndex : this.#index;
    let index;
    if (event.key === 'ArrowRight') index = currentIndex + (this.#rtl ? -1 : 1);
    else if (event.key === 'ArrowLeft') index = currentIndex + (this.#rtl ? 1 : -1);
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = this.#cards.length - 1;
    else return;
    event.preventDefault();
    this.#select((index + this.#cards.length) % this.#cards.length, true, moveFocus);
  }

  /** @param {number} direction @param {boolean} announce */
  #advance(direction, announce) {
    this.#sync();
    let index = this.#index + direction;
    if (direction > 0 && this.#atEnd) index = 0;
    else if (direction < 0 && this.#atStart) index = this.#cards.length - 1;
    this.#select(Math.max(0, Math.min(this.#cards.length - 1, index)), announce);
  }

  /** @param {number} index @param {boolean} announce @param {boolean} [moveFocus=false] */
  #select(index, announce, moveFocus = false) {
    const card = this.#cards[index];
    const { scroller, status } = this.refs;
    if (!card || !this.#scroll) return;
    const rectangle = card.getBoundingClientRect();
    const bounds = scroller.getBoundingClientRect();
    const padding = parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
    const delta = this.#rtl ? rectangle.right - bounds.right + padding : rectangle.left - bounds.left - padding;
    this.#scroll.to(scroller.scrollLeft + delta, { instant: Boolean(this.#motion?.matches) });
    if (moveFocus) card.querySelector('a')?.focus({ preventScroll: true });
    if (announce && status) status.textContent = (this.dataset.positionTemplate ?? '').replace('{index}', `${index + 1}`).replace('{count}', `${this.#cards.length}`);
    this.#schedule();
  }

  #onPointerEnter = () => { this.#hovered = true; this.#stop(); };
  #onPointerLeave = () => { this.#hovered = false; this.#schedule(); };
  #onPointerDown = () => { this.#pointerActive = true; this.#stop(); };
  #onPointerUp = () => { this.#pointerActive = false; this.#schedule(); };

  #onFocusIn = () => { this.#focused = true; this.#stop(); };
  #onFocusOut = () => {
    queueMicrotask(() => {
      if (!this.isConnected || !this.#controller || this.#controller.signal.aborted) return;
      this.#focused = this.contains(document.activeElement);
      this.#schedule();
    });
  };
}

if (!customElements.get('catalogue-product-carousel')) {
  customElements.define('catalogue-product-carousel', CatalogueProductCarousel);
}
